import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useState } from "react";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import zatcaLogo from "src/assets/img/zatca.png";
import {
  useDisConnectZatca,
  useGetZatcaStatus,
} from "src/api/integrations/zatca";
import CompanyInfoPopup from "./company-info-popup";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { useOneBranchHasPermission } from "src/shared/hooks/has-permission-hook";
import { IntegrationsPermissions } from "src/constants/permissions";

const ZatcaIntegrationCard = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const setNotifications = useSetRecoilState(notificationsState);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [open, setOpen] = useState(false);

  // apis
  const { data: statusData, isFetching, refetch } = useGetZatcaStatus();
  const { mutate, isPending } = useDisConnectZatca();

  // permissions
  const hasABranchPermission = useOneBranchHasPermission();
  const connectIntegrationsPermission = hasABranchPermission(
    IntegrationsPermissions.connectIntegration,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCloseConfirm = () => setOpenConfirm(false);
  const handleOpenConfirm = () => setOpenConfirm(true);

  const handleConfirm = () => {
    mutate(undefined, {
      onSuccess: () => {
        refetch();
        handleCloseConfirm();
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: `${t("status.disconnected")}`,
          },
        ]);
      },
      onError: () => {
        setNotifications((prev) => [
          ...prev,
          {
            type: "error",
            message: `${t("errors.disconnectFailed")}`,
          },
        ]);
      },
    });
  };

  if (isFetching) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "12px",
          height: "100%",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Card>
    );
  }

  const isConnected = statusData?.data.fai.connected;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        height: "100%",
        transition: "box-shadow 0.2s",
        "&:hover": {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box
              component="img"
              src={zatcaLogo}
              alt="Foodics"
              width={{ xs: 40, sm: 48 }}
              height={{ xs: 40, sm: 48 }}
              sx={{ objectFit: "contain" }}
            />
            <Chip
              size="small"
              label={
                isConnected ? t("status.connected") : t("status.disconnected")
              }
              color={isConnected ? "success" : "default"}
              sx={{
                fontWeight: 500,
                fontSize: { xs: "11px", sm: "12px" },
                height: { xs: 24, sm: 28 },
              }}
            />
          </Box>

          {/* Title & Description */}
          <Box>
            <Typography
              fontSize={{ xs: 16, sm: 18 }}
              fontWeight={600}
              color="#101828"
            >
              {t("zatca.title")}
            </Typography>
            <Typography fontSize={{ xs: 13, sm: 14 }} color="#667085" mt={0.5}>
              {t("zatca.description")}
            </Typography>
          </Box>

          {/* Actions */}
          {connectIntegrationsPermission ? (
            <Stack direction="column" spacing={1} mt={1}>
              {isConnected ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <SettingsIcon sx={{ fontSize: "16px !important" }} />
                  }
                  onClick={handleOpenConfirm}
                  fullWidth
                  sx={{
                    fontSize: "12px",
                    py: 1,
                    "& .MuiButton-startIcon": {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {t("actions.disconnect")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  startIcon={<LinkIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={handleOpen}
                  sx={{
                    fontSize: "12px",
                    py: 1,
                  }}
                >
                  {t("actions.connect")}
                </Button>
              )}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>

      <CompanyInfoPopup
        open={open}
        handleClose={handleClose}
        refetch={refetch}
      />

      {/* confirm close popup */}
      <ConfirmPopup
        open={openConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleConfirm}
        title={t("actions.disconnect")}
        subtitle={generalT("areYouSureToConfirm")}
        confirmBtnText={generalT("confirm")}
        isPending={isPending}
      />
    </Card>
  );
};

export default ZatcaIntegrationCard;
